/**
 * RESOURCES — WHAT SCOUT SPENT, WHAT THE FIGURES ARE COMPUTED FROM, AND WHERE
 * THEY ARE KNOWN TO BE INCOMPLETE.
 *
 * THE ONE THING THIS SCREEN EXISTS TO NOT DO IS PRINT A CONFIDENT ZERO, AND THE
 * SECOND THING IS TO NOT PRINT A CONFIDENT FALSEHOOD — WHICH THIS SCREEN DID.
 *
 * WHAT IT USED TO SAY, AND WHY IT WAS WRONG. Its first version told the Owner
 * that `usage.infrastructure.researchFetches` sums activation rows only and that
 * the research-window lane never creates one, "so a window's fetches cannot reach
 * this figure at all". That sentence was taken from an architecture document and
 * never checked against the code. It is false. Every batch of a research window
 * records an activation carrying the searches it ran and the sources it read
 * (`scout-intelligence.ts`, the batch loop and `completeActivation`), the sum
 * reads every activation row for this owner with no research filter on it, and a
 * window's fetches have always reached the figure. A screen that told the Owner
 * their window could not appear here would have them disbelieve a number that
 * was counting it — the exact failure this product's honesty rules exist to
 * prevent, committed by a screen written to enforce them.
 *
 * SO THE LIMIT STATED IS THE ONE THAT IS REAL, and it is narrower and duller:
 * the read takes the NEWEST FIFTY activation rows, so a longer history reads
 * short. That is what the panel says, because that is what is true.
 *
 * `usage.model` IS A WINDOW, NOT A TOTAL — bounded by the admission window's own
 * length. It is the right number for the budget it governs and the wrong number
 * for "what has this ever cost".
 *
 * SLICE 8 ADDED THE LEDGER UNDER THE FIGURES, and it is what makes the rest of
 * this screen checkable. `/gateway/usage` pages through `scout_model_spend`
 * itself — the same rows every total here is summed from — and carries its own
 * coverage, so "71 requests" finally has rows that can be counted to
 * seventy-one. It is also the one read that names the provider and model, under
 * the Owner-private exception recorded in `core/disclosure.ts`; the panel below
 * says so rather than leaving a reader to wonder whether the boundary had simply
 * lapsed. The run-scoped line the audit called G7 rides the same read: a run's
 * own calls are recorded with `run_id` and nothing read it back before this.
 *
 * AND THE WINDOW'S SPEND IS SCOPED OUT OF SOMETHING REAL, which is worth saying
 * because the true half of the old claim was in there somewhere: a run's
 * activations are excluded from `usage.admission`, the CONVERSATION budget, so
 * an unattended window cannot spend the Owner's own allowance. That exclusion is
 * proved by specimens; the one the screen claimed was not.
 *
 * NO MONEY FIGURE IS DERIVED FOR SEARCH. The backend refuses to: a search
 * provider bills in its own units and this repository holds no price authority
 * for one. The refusal is rendered as the backend states it rather than
 * paraphrased into an estimate.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { Failure } from './failure';
import { PageHeader } from './about';

/**
 * ONE ROW OF THE MODEL LEDGER.
 *
 * THE TOKEN COUNTS ARE ALWAYS NUMBERS, AND THAT IS NOT THE SAME AS BEING
 * MEASURED. `scout_model_spend` declares both token columns `NOT NULL`: a call
 * that reached the provider is charged whether or not it reported usage, so the
 * row always carries a count — and when the provider did not report one, the
 * row carries SCOUT'S ESTIMATE and says so in `usageSource`. The distinction
 * this screen has to render is therefore NOT "number or blank", it is
 * "the provider's own count or an inference", and `reported` is what carries
 * it. A screen that printed the estimate without its label would present an
 * inferred figure as a measured one.
 */
interface LedgerRow {
	spendId: string;
	at: string;
	purpose: string;
	identity: { provider: string | null; model: string | null };
	providerRequests: number;
	tokens: { input: number; output: number; reported: boolean };
	usageSource: string;
	cost: { microusd: number | null; source: string };
	attribution: {
		activationId: string | null;
		missionId: string | null;
		runId: string | null;
		targetKey: string | null;
	};
}

interface Ledger {
	scope: { runId: string | null; runScoped: boolean; note: string };
	rows: LedgerRow[];
	totals: {
		providerRequests: number;
		inputTokens: number;
		outputTokens: number;
		pricedRows: number;
		pricedSubtotalMicrousd: number | null;
		rows: number;
		costSource: string;
		basis: string;
	};
	coverage: {
		shown: number;
		total: number;
		hasMore: boolean;
		nextBefore: string | null;
		truncated: boolean;
	};
	notRecorded: Array<{ fact: string; reason: string }>;
}

interface CostBlock {
	microusd: number | null;
	source: string;
	pricedSubtotalMicrousd: number | null;
	rows: number;
	pricedRows: number;
	unpriceableRows: number;
	basis: string;
}

interface ResourceAccount {
	window: { scope: string; windowMs: number; from: string; to: string; note: string };
	model: {
		providerRequests: number;
		tokens: { input: number; output: number };
		usageSource: string;
		cost: CostBlock;
	};
	search: {
		providers: Array<{ providerKey: string; requests: number; credits: number | null; estimatedRows: number }>;
		rows: number;
		pricedRows: number;
		unpriceableRows: number;
		costAuthority: string;
		note: string;
	};
	notAvailable: Array<{ category: string; reason: string }>;
}

interface HistoryPayload {
	usage: {
		classes: string[];
		model: { providerRequests: number; inputTokens: number; outputTokens: number };
		admission: {
			scope: string;
			windowMs: number;
			providerRequestsUsed: number;
			providerRequestsRemaining: number | null;
			tokensUsed: number;
			tokensRemaining: number | null;
			oldestSpendExitsAt: string | null;
		};
		search: Array<{ providerKey: string; requests: number; credits: number | null }>;
		infrastructure: { researchFetches: number; storageWrites: number };
	};
}

interface Capabilities {
	resourceAccount: ResourceAccount;
}

/** THE RUN IS THE JOIN BETWEEN A SPEND ROW AND A WORKSPACE. The ledger's
 *  attribution already carries the run id; the run record carries the space
 *  and the topic intent it recorded. No new mapping table is invented. */
interface SpendRun {
	runId: string;
	spaceId?: string | null;
	topicIntent?: string | null;
	subjectText?: string | null;
}

interface SpendRunsPayload {
	runs: SpendRun[];
}

interface SpendSpacesPayload {
	spaces: Array<{ spaceId: string; name: string }>;
}

function asMicrosUsd(value: number | null): string {
	if (value === null) return 'unpriced';
	return `$${(value / 1_000_000).toFixed(4)}`;
}

function minutes(ms: number): string {
	return `${Math.round(ms / 60_000)} min`;
}

/** WHAT THE TOKEN COLUMNS ARE. The count travels with whose count it is, so a
 *  reader never has to know the ledger's rules to read the number correctly. */
function tokenSourceLabel(reported: boolean): string {
	return reported ? 'provider-reported' : 'inferred by Scout — the provider did not report';
}

function callTime(value: string): string {
	return value.replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

interface SpendGroup {
	key: string;
	requests: number;
	input: number;
	output: number;
	microusd: number;
	priced: number;
	rows: number;
}

/** SUMS OVER THE ROWS THE PAGE HAS READ, AND NOTHING ELSE. The key function
 *  decides what a group is; a null key is named rather than dropped, so the
 *  totals of the breakdowns always add up to the ledger's own visible rows. */
function groupSpend(rows: readonly LedgerRow[], keyOf: (row: LedgerRow) => string): SpendGroup[] {
	const groups = new Map<string, SpendGroup>();
	for (const row of rows) {
		const key = keyOf(row);
		const current = groups.get(key) ?? {
			key,
			requests: 0,
			input: 0,
			output: 0,
			microusd: 0,
			priced: 0,
			rows: 0,
		};
		current.requests += row.providerRequests;
		current.input += row.tokens.input;
		current.output += row.tokens.output;
		current.rows += 1;
		if (row.cost.microusd !== null) {
			current.microusd += row.cost.microusd;
			current.priced += 1;
		}
		groups.set(key, current);
	}
	return [...groups.values()].sort((left, right) => right.microusd - left.microusd || right.requests - left.requests);
}

function SpendTable({ title, groups, firstColumn }: { title: string; groups: SpendGroup[]; firstColumn: string }) {
	if (groups.length === 0) return null;
	return (
		<section class="panel panel-flush" aria-labelledby={`spend-${title.replaceAll(' ', '-')}`}>
			<div class="section-head">
				<h2 id={`spend-${title.replaceAll(' ', '-')}`}>{title}</h2>
				<span class="section-note">{groups.length} group(s)</span>
			</div>
			<div class="table-wrap">
				<table class="data-table">
					<thead>
						<tr>
							<th scope="col">{firstColumn}</th>
							<th scope="col" class="numeric">Requests</th>
							<th scope="col" class="numeric">Tokens in</th>
							<th scope="col" class="numeric">Tokens out</th>
							<th scope="col" class="numeric">Priced rows</th>
							<th scope="col" class="numeric">Cost</th>
						</tr>
					</thead>
					<tbody>
						{groups.slice(0, 12).map((group) => (
							<tr key={group.key}>
								<td class="cell-title" data-label={firstColumn}>
									<span>{group.key}</span>
								</td>
								<td class="numeric" data-label="Requests">
									{group.requests}
								</td>
								<td class="numeric" data-label="Tokens in">
									{group.input.toLocaleString()}
								</td>
								<td class="numeric" data-label="Tokens out">
									{group.output.toLocaleString()}
								</td>
								<td class="numeric" data-label="Priced rows">
									{group.priced}/{group.rows}
								</td>
								<td class="numeric" data-label="Cost">
									{asMicrosUsd(group.microusd)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	);
}

function SpendBreakdown({
	rows,
	workspaceOf,
	topicOf,
}: {
	rows: readonly LedgerRow[];
	workspaceOf: (row: LedgerRow) => string;
	topicOf: (row: LedgerRow) => string;
}) {
	if (rows.length === 0) return null;
	const byDay = groupSpend(rows, (row) => row.at.slice(0, 10));
	const byModel = groupSpend(
		rows,
		(row) =>
			`${row.identity.provider ?? 'provider unstated'} / ${row.identity.model ?? 'model unstated'}`,
	);
	const byWorkspace = groupSpend(rows, workspaceOf);
	const byTopic = groupSpend(rows, topicOf);
	const byRun = groupSpend(rows, (row) => row.attribution.runId ?? 'unattributed (no run)');
	// ONE FULL-WIDTH TABLE PER GROUPING. Side-by-side spend tables were tried
	// and clipped their cost column at 1920: a table is a width-hungry object,
	// and the dashboard grid is for panels that answer short questions.
	return (
		<div class="stack">
			<SpendTable title="By day" groups={byDay} firstColumn="Day (UTC)" />
			<SpendTable title="By model" groups={byModel} firstColumn="Provider / model" />
			<SpendTable title="By workspace" groups={byWorkspace} firstColumn="Research workspace" />
			<SpendTable title="By research subject" groups={byTopic} firstColumn="Recorded topic intent" />
			<SpendTable title="By run" groups={byRun} firstColumn="Research window / run" />
		</div>
	);
}

export function ResourcesView() {
	const history = useMemo(
		() => createLoadable<HistoryPayload>(() => readGateway<HistoryPayload>('/api/scout/gateway/history')),
		[],
	);
	const capabilities = useMemo(
		() => createLoadable<Capabilities>(() => readGateway<Capabilities>('/api/scout/gateway/capabilities')),
		[],
	);
	const ledger = useMemo(
		() => createLoadable<Ledger>(() => readGateway<Ledger>('/api/scout/gateway/usage')),
		[],
	);
	const runs = useMemo(
		() =>
			createLoadable<SpendRunsPayload>(() =>
				readGateway<SpendRunsPayload>('/api/scout/gateway/research/runs'),
			),
		[],
	);
	const spaces = useMemo(
		() =>
			createLoadable<SpendSpacesPayload>(() =>
				readGateway<SpendSpacesPayload>('/api/scout/gateway/spaces?limit=200'),
			),
		[],
	);
	useEffect(() => {
		void history.load();
		void capabilities.load();
		void ledger.load();
		void runs.load();
		void spaces.load();
	}, [history, capabilities, ledger, runs, spaces]);

	const historyState = history.state.value;
	const capsState = capabilities.state.value;
	const ledgerState = ledger.state.value;
	const account = capsState.data?.resourceAccount ?? null;
	const usage = historyState.data?.usage ?? null;
	const firstPage = ledgerState.data ?? null;

	const [older, setOlder] = useState<LedgerRow[]>([]);
	/** `undefined` until the Owner pages: the first page's own cursor is the next
	 *  one. After that this holds the cursor Scout last issued, so a paging
	 *  mistake cannot be hidden by re-deriving it from the rows on screen. */
	const [next, setNext] = useState<string | null | undefined>(undefined);
	const [paging, setPaging] = useState(false);
	const [pagingError, setPagingError] = useState<GatewayError | null>(null);

	const rows = firstPage === null ? older : [...firstPage.rows, ...older];
	const cursor = next === undefined ? (firstPage?.coverage.nextBefore ?? null) : next;

	const loadOlder = useCallback(async () => {
		if (cursor === null) return;
		setPaging(true);
		setPagingError(null);
		const result = await readGateway<Ledger>(
			`/api/scout/gateway/usage?before=${encodeURIComponent(cursor)}`,
		);
		setPaging(false);
		if (!result.ok) {
			setPagingError(result.error);
			return;
		}
		setOlder((previous) => [...previous, ...result.data.rows]);
		setNext(result.data.coverage.nextBefore);
	}, [cursor]);

	return (
		<section class="view" aria-labelledby="resources-heading">
			<PageHeader
				id="resources-heading"
				title="Resources"
				about={{
					purpose:
						'What Scout is metered on spending, what each figure is computed from, and what this deployment cannot account for.',
					limits: (
						<>
							<p>
								<strong>No billing figure is invented.</strong> The model cost is a catalog
								envelope — an upper bound computed from the platform catalog’s declared credit cost
								for the model that ran. It is not a provider-confirmed charge and not a bill.
							</p>
							<p>
								<strong>Which model ran is on this screen and nowhere else.</strong> The Owner ruled
								on 2026-10-16 that this one private workspace may name the provider and model,
								because a usage record that could not say what it was a record of would not be one.
								No key, header, secret or prompt is read onto any surface.
							</p>
							<p>
								<strong>Two of the numbers here are known to undercount.</strong> The model figures
								are a window rather than a total, and the infrastructure counters cover activation
								rows only.
							</p>
						</>
					),
				}}
			/>

			{capsState.status === 'failed' && capsState.error !== null && (
				<Failure
					error={capsState.error}
					what="the resource account"
					onRetry={() => void capabilities.load()}
				/>
			)}

			{account !== null && (
				<>
					<section class="panel" aria-labelledby="account-heading">
						<h2 id="account-heading">The account</h2>
						<p class="muted small">
							Window: {account.window.scope} · {minutes(account.window.windowMs)} ·{' '}
							<time dateTime={account.window.from}>{account.window.from}</time> to{' '}
							<time dateTime={account.window.to}>{account.window.to}</time>
						</p>
						<p class="notice small">{account.window.note}</p>

						<dl class="facts">
							<div>
								<dt>Model requests</dt>
								<dd>
									<span class="count">{account.model.providerRequests}</span>
									<span class="muted small"> · counted from {account.model.usageSource}</span>
								</dd>
							</div>
							<div>
								<dt>Tokens</dt>
								<dd>
									<span class="count">{account.model.tokens.input}</span> in ·{' '}
									<span class="count">{account.model.tokens.output}</span> out
								</dd>
							</div>
							<div>
								<dt>Cost</dt>
								<dd>
									{asMicrosUsd(account.model.cost.microusd)}
									<span class="muted small">
										{' '}
										· {account.model.cost.pricedRows} of {account.model.cost.rows} row(s) priced,{' '}
										{account.model.cost.unpriceableRows} unpriceable
									</span>
								</dd>
							</div>
						</dl>
						{/* THE BASIS IS THE PART THAT STOPS A NUMBER BEING READ AS A BILL.
						    It is the backend's own sentence, rendered verbatim. */}
						<p class="authority">{account.model.cost.basis}</p>

						<h3 class="subhead">Search</h3>
						{account.search.providers.length === 0 ? (
							<p>
								<strong>No search provider has been used in this window.</strong>{' '}
								<span class="muted">
									That is a statement about the window, not about whether search is configured —
									a configured provider that was never called records nothing.
								</span>
							</p>
						) : (
							<ul class="records">
								{account.search.providers.map((provider) => (
									<li key={provider.providerKey} class="record">
										<div class="record-head">
											<code>{provider.providerKey}</code>
										</div>
										<p class="muted small">
											{provider.requests} request(s) ·{' '}
											{provider.credits === null ? 'credits unknown' : `${provider.credits} credit(s)`} ·{' '}
											{provider.estimatedRows} estimated row(s)
										</p>
									</li>
								))}
							</ul>
						)}
						<p class="notice small">{account.search.note}</p>
					</section>

					{/* ------------------------------------------ spend breakdown */}
					<SpendBreakdown
						rows={firstPage?.rows ?? []}
						workspaceOf={(row) => {
							const run =
								row.attribution.runId === null
									? undefined
									: (runs.state.value.data?.runs ?? []).find(
											(entry) => entry.runId === row.attribution.runId,
										);
							if (run === undefined) return 'unattributed (no run on this page)';
							if (run.spaceId === null || run.spaceId === undefined) return 'Main Scout';
							return (
								(spaces.state.value.data?.spaces ?? []).find(
									(space) => space.spaceId === run.spaceId,
								)?.name ?? run.spaceId
							);
						}}
						topicOf={(row) => {
							const run =
								row.attribution.runId === null
									? undefined
									: (runs.state.value.data?.runs ?? []).find(
											(entry) => entry.runId === row.attribution.runId,
										);
							if (run === undefined) return 'unattributed (no run on this page)';
							return run.topicIntent ?? run.subjectText ?? 'no recorded topic intent';
						}}
					/>
					<p class="notice small">
						These breakdowns are sums over the rows this page has read. A row whose cost could
						not be priced is counted in requests but contributes no dollars; the currency figure
						remains the CATALOG ENVELOPE described above, not a provider bill.
					</p>

					{/* ------------------------------------------------ the ledger */}
					<section class="panel" aria-labelledby="ledger-heading">
						<h2 id="ledger-heading">The model ledger</h2>
						<p class="view-blurb">
							Every metered model call Scout has recorded, newest first — the rows the account
							above is summed from.
						</p>

						{ledgerState.status === 'loading' && (
							<p class="notice" role="status">
								Reading the ledger…
							</p>
						)}
						{ledgerState.status === 'failed' && ledgerState.error !== null && (
							<Failure
								error={ledgerState.error}
								what="the model ledger"
								onRetry={() => void ledger.load()}
							/>
						)}

						{firstPage !== null && (
							<>
								{/* THE EXCEPTION, STATED WHERE IT IS USED. Every other surface in
								    this workspace withholds the provider and model on purpose; this
								    one names them because the Owner ruled that an accounting ledger
								    that cannot say what it is a ledger OF is not an accounting
								    surface. Saying so is what keeps the exception from reading as a
								    boundary that quietly stopped being enforced somewhere else. */}
								<p class="notice small">
									<strong>This is the one screen that names the reasoning provider and
									model.</strong>{' '}
									Everywhere else in Scout they are deliberately withheld. Here they are the
									subject: a usage record that could not say which model ran would not be one.
									The Owner ruled on 2026-10-16 that this one private workspace may name them;
									the exception releases exactly two strings — the provider key and the model id.
									No key, header, secret or prompt is read onto any surface.
								</p>

								<p class="muted small">{firstPage.scope.note}</p>

								<dl class="facts">
									<div>
										<dt>Shown</dt>
										<dd>
											<span class="count">{rows.length}</span>
											<span class="muted small"> of {firstPage.coverage.total} recorded call(s)</span>
										</dd>
									</div>
									<div>
										<dt>Requests in these {firstPage.totals.rows}</dt>
										<dd>
											<span class="count">{firstPage.totals.providerRequests}</span>
										</dd>
									</div>
									<div>
										<dt>Tokens in the same set</dt>
										<dd>
											<span class="count">{firstPage.totals.inputTokens}</span> in ·{' '}
											<span class="count">{firstPage.totals.outputTokens}</span> out
										</dd>
									</div>
									<div>
										<dt>Priced subtotal</dt>
										<dd>
											{asMicrosUsd(firstPage.totals.pricedSubtotalMicrousd)}
											<span class="muted small">
												{' '}
												· {firstPage.totals.costSource} · {firstPage.totals.pricedRows} of{' '}
												{firstPage.totals.rows} row(s) priced
											</span>
										</dd>
									</div>
								</dl>
								<p class="authority">{firstPage.totals.basis}</p>

								{firstPage.coverage.truncated && (
									<p class="notice">
										<strong>This ledger is longer than one page.</strong> The figures above
										cover all {firstPage.coverage.total} recorded call(s); the list below shows{' '}
										{rows.length} of them.
									</p>
								)}

								{rows.length === 0 ? (
									<p>
										<strong>No model call is on record.</strong>{' '}
										<span class="muted">
											That is a statement about the ledger, not about whether a provider is
											configured — a configured provider that was never asked records nothing.
										</span>
									</p>
								) : (
									<ul class="records">
										{rows.map((row) => (
											<li key={row.spendId} class="record">
												<div class="record-head">
													<code>{row.purpose}</code>{' '}
													<span class="muted small">{callTime(row.at)}</span>
												</div>
												<p class="muted small">
													<span class="tag">{row.identity.provider ?? 'provider not recorded'}</span>{' '}
													<span class="tag">{row.identity.model ?? 'model not recorded'}</span>
												</p>
												<p class="muted small">
													{row.providerRequests} request(s) ·{' '}
													<span class="count">{row.tokens.input}</span> in ·{' '}
													<span class="count">{row.tokens.output}</span> out ·{' '}
													{tokenSourceLabel(row.tokens.reported)}
												</p>
												<p class="muted small">
													Cost: {asMicrosUsd(row.cost.microusd)}{' '}
													<span class="muted">({row.cost.source})</span>
													{row.attribution.runId !== null && (
														<>
															{' '}
															· run <code>{row.attribution.runId}</code>
														</>
													)}
													{row.attribution.activationId !== null && (
														<>
															{' '}
															· activation <code>{row.attribution.activationId}</code>
														</>
													)}
												</p>
											</li>
										))}
									</ul>
								)}

								{pagingError !== null && (
									<Failure
										error={pagingError}
										what="the older calls"
										onRetry={() => void loadOlder()}
									/>
								)}
								{cursor !== null && (
									<button type="button" onClick={() => void loadOlder()} disabled={paging}>
										{paging ? 'Reading older calls…' : 'Load older calls'}
									</button>
								)}
								{cursor === null && rows.length > 0 && (
									<p class="muted small">That is the whole ledger as it stands.</p>
								)}

								{/* ------------------------------------------------ what the rows cannot say */}
								<h3 class="subhead">What a row cannot tell you</h3>
								<ul>
									{firstPage.notRecorded.map((entry) => (
										<li key={entry.fact}>
											<strong>{entry.fact}</strong> — {entry.reason}
										</li>
									))}
								</ul>
							</>
						)}
					</section>

					{/* -------------------------------------- what is NOT accounted */}
					<section class="panel not-known" aria-labelledby="not-available-heading">
						<h2 id="not-available-heading">What this account does not cover</h2>
						<ul>
							{account.notAvailable.map((entry) => (
								<li key={entry.category}>
									<strong>
										<code>{entry.category}</code>
									</strong>{' '}
									— {entry.reason}
								</li>
							))}
						</ul>
					</section>
				</>
			)}

			{/* ------------------------------------------------ coverage limits */}
			<section class="panel" aria-labelledby="infrastructure-heading">
				<h2 id="infrastructure-heading">Infrastructure counters, and why they are not totals</h2>
				{historyState.status === 'loading' && (
					<p class="notice" role="status">
						Reading the operational counters…
					</p>
				)}
				{historyState.status === 'failed' && historyState.error !== null && (
					<Failure
						error={historyState.error}
						what="the operational counters"
						onRetry={() => void history.load()}
					/>
				)}
				{usage !== null && (
					<>
						<dl class="facts">
							<div>
								<dt>Research fetches</dt>
								<dd>
									<span class="count">{usage.infrastructure.researchFetches}</span>
								</dd>
							</div>
							<div>
								<dt>Storage writes</dt>
								<dd>
									<span class="count">{usage.infrastructure.storageWrites}</span>
								</dd>
							</div>
						</dl>
						{/* THE LIMIT IS THE POINT OF THE PANEL — AND IT IS NOT THE LIMIT
						    THIS PANEL USED TO STATE.

						    AN EARLIER VERSION OF THIS SCREEN SAID A WINDOW'S FETCHES
						    "CANNOT REACH THESE NUMBERS AT ALL", QUOTING AN ARCHITECTURE
						    DOCUMENT RATHER THAN READING THE CODE. That is false, and it
						    was false when it shipped: the research-window lane's every
						    batch records an activation carrying what it searched and
						    read, and the sum below reads every activation row for this
						    owner with no research filter on it. A screen that told the
						    Owner their window's activity could not appear here would
						    have them disbelieve a figure that was counting it.

						    THE REAL LIMIT IS THE BOUND, and it is narrower and duller:
						    the read takes the newest fifty activation rows, so a store
						    with a longer history than that reads short. That is what is
						    stated, because it is what is true. */}
						<p class="notice">
							<strong>These are not totals.</strong> Both figures are summed over the{' '}
							<em>newest fifty</em> activation rows for this owner — not over every row the
							store holds — so a longer history reads short. A zero means no activation in
							that window fetched anything.
						</p>
						<p class="muted small">
							A research window’s fetches <strong>do</strong> reach these numbers: each of its
							batches records an activation carrying the searches it ran and the sources it
							read. What a window’s spend is deliberately scoped out of is the{' '}
							<em>conversation admission budget</em> below, so an unattended window cannot
							spend the Owner’s own allowance. Reconciling the fifty-row bound is slice 8’s
							work.
						</p>
						<h3 class="subhead">The admission window</h3>
						<p class="muted small">
							What the budget a refusal is derived from currently holds — a {usage.admission.scope}{' '}
							scope over {minutes(usage.admission.windowMs)}.
						</p>
						<dl class="facts">
							<div>
								<dt>Model requests used</dt>
								<dd>
									<span class="count">{usage.admission.providerRequestsUsed}</span>
									{usage.admission.providerRequestsRemaining !== null && (
										<span class="muted small">
											{' '}
											· {usage.admission.providerRequestsRemaining} remaining
										</span>
									)}
								</dd>
							</div>
							<div>
								<dt>Tokens used</dt>
								<dd>
									<span class="count">{usage.admission.tokensUsed}</span>
									{usage.admission.tokensRemaining !== null && (
										<span class="muted small">
											{' '}
											· {usage.admission.tokensRemaining} remaining
										</span>
									)}
								</dd>
							</div>
							{usage.admission.oldestSpendExitsAt !== null && (
								<div>
									<dt>Oldest spend leaves the window</dt>
									<dd>
										<time dateTime={usage.admission.oldestSpendExitsAt}>
											{usage.admission.oldestSpendExitsAt}
										</time>
									</dd>
								</div>
							)}
						</dl>
					</>
				)}
			</section>
		</section>
	);
}
